/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Wrappers around Word custom XML parts.
 * Used for storing paragraph UUIDs, signature metadata,
 * and document-level Ecsper metadata outside visible content.
 *
 * Namespace convention: 'http://ecsper.io/schema/{kind}/{version}'
 *   Examples:
 *     - http://ecsper.io/schema/paragraph-uuids/1
 *     - http://ecsper.io/schema/signature/1
 *     - http://ecsper.io/schema/document-metadata/1
 */

export interface CustomXmlPart {
  /** Word's internal part ID */
  id: string;
  /** XML namespace URI */
  namespaceUri: string;
  /** Full XML body */
  xml: string;
}

export async function addCustomXmlPart(_namespaceUri: string, xml: string): Promise<CustomXmlPart> {
  return Word.run(async (context) => {
    const part = context.document.customXmlParts.add(xml);
    part.load(['id', 'namespaceUri']);
    const xmlResult = part.getXml();
    await context.sync();
    return {
      id: part.id,
      namespaceUri: part.namespaceUri,
      xml: xmlResult.value,
    };
  });
}

export async function findCustomXmlPartByNs(namespaceUri: string): Promise<CustomXmlPart | null> {
  return Word.run(async (context) => {
    const scoped = context.document.customXmlParts.getByNamespace(namespaceUri);
    scoped.load('items');
    await context.sync();
    if (scoped.items.length === 0) return null;
    const part = scoped.items[0];
    if (part === undefined) return null;
    part.load(['id', 'namespaceUri']);
    const xmlResult = part.getXml();
    await context.sync();
    return {
      id: part.id,
      namespaceUri: part.namespaceUri,
      xml: xmlResult.value,
    };
  });
}

export async function readCustomXmlPart(id: string): Promise<CustomXmlPart | null> {
  return Word.run(async (context) => {
    const parts = context.document.customXmlParts;
    parts.load('items');
    await context.sync();
    for (const p of parts.items) {
      p.load(['id']);
    }
    await context.sync();
    const found = parts.items.find((p) => p.id === id);
    if (found === undefined) return null;
    found.load(['namespaceUri']);
    const xmlResult = found.getXml();
    await context.sync();
    return {
      id: found.id,
      namespaceUri: found.namespaceUri,
      xml: xmlResult.value,
    };
  });
}

export async function deleteCustomXmlPart(id: string): Promise<void> {
  await Word.run(async (context) => {
    const parts = context.document.customXmlParts;
    parts.load('items');
    await context.sync();
    for (const p of parts.items) {
      p.load(['id']);
    }
    await context.sync();
    const found = parts.items.find((p) => p.id === id);
    if (found === undefined) return;
    found.delete();
    await context.sync();
  });
}
